/**
 * The books a learner is part-way through, as a shelf.
 *
 * The device already knows every book's place (see `#/lib/flashbookProgressStore`)
 * and the reader can already resume one once it is opened. What was missing is the
 * *door*: a learner halfway through a hundred-page book has to remember which books
 * they are in the middle of and find each one again in the feed — and a book read
 * over days is exactly the thing nobody remembers the name of.
 *
 * This is the pure half of that door: what belongs on the shelf, in what order, and
 * how far in each one is. The rules are worth stating because each is a decision
 * rather than a filter:
 *
 *  - **Part-way means a read that is still open.** A book they finished is not a
 *    book to continue, and a book whose read ended unfinished *is* — the store
 *    already tells the two apart (`open` versus the register), so the shelf reads
 *    that rather than re-deciding it. A book the learner has only ever *finished*
 *    still has an entry in the index, and it is deliberately not on the shelf:
 *    the way back into a finished book is the report, not a resume.
 *
 *  - **Ordered by when it was last read, newest first.** `updatedAt` is written on
 *    every save, so it is the moment of the last page turn rather than the moment
 *    the book was opened — which is the same thing the store's own trim uses, so
 *    the book that falls off the shelf last is the book that has waited longest.
 *
 *  - **How far in is pages, not exercises.** The reader's own progress ring counts
 *    *answered exercises*, which needs the whole book to divide by; the shelf has
 *    only what is on the device, and asking for a book's pages to draw a shelf of
 *    them would turn a list into a fan-out of requests. Pages are also the honest
 *    answer to "how far in": `pageIndex` is the page behind the reader.
 *
 * A surface that has *one* book in front of it — a feed card deciding whether to say
 * "Read the book" or "Continue" — asks a narrower version of the same question and
 * gets it from `flashBookPlace`, which borrows the reader's own rule for what counts
 * as something to continue.
 *
 * Pure, and deliberately: it is a function of the stored index and nothing else, so
 * every rule above can be tested without a device or a screen.
 */
import {
  type FlashBookOpenRead,
  type FlashBookReadProgress,
  resumeTarget,
} from '#/lib/flashbookProgress'
import {type FlashBookProgressIndex} from '#/lib/flashbookProgressStore'

/**
 * How many books the shelf will show.
 *
 * Bounded for the same reason the store is: a learner who has opened a lot of books
 * should get a shelf rather than a scroll that never ends, and the books that fall
 * off are the ones waiting longest — the same rule the store's own trim uses.
 */
export const SHELF_LIMIT = 10

/** One book on the shelf: where it is, and how far in. */
export type FlashBookShelfItem = {
  /** The book's record URI, which is what opens it. */
  bookUri: string
  /**
   * The title as it was when the place was last written.
   *
   * Carried on the place rather than fetched, so the shelf has its words on the
   * first frame. It may be empty on a place written before a book had a title, and
   * the caller is the one that decides what to say instead.
   */
  bookTitle: string
  /** The page the reader is on, 1-based, as a reader counts pages. */
  page: number
  pageCount: number
  /** Pages *behind* the reader — the page they are on is not one of them. */
  pagesRead: number
  /** How far in, 0…1, by pages. */
  pct: number
  /** When this book was last read, which is what orders the shelf. */
  lastReadAt: string
}

/**
 * The shelf, from the device's index of reading places.
 *
 * Tolerant of what it finds rather than trusting it, and for the same reason the
 * cross-device reader is: the index is storage, it survives upgrades, and a shape
 * written by an older build of the app is not a shape the caller can render. A
 * place with no open read is skipped; a place whose pages cannot be counted is
 * still shown, because *where* a learner is matters more than the fraction of the
 * book it represents — a book with no page count is on the shelf with its page
 * number and no rail, not hidden.
 */
export function buildFlashBookShelf(
  index: FlashBookProgressIndex | null | undefined,
  {limit = SHELF_LIMIT}: {limit?: number} = {},
): FlashBookShelfItem[] {
  if (!index) return []
  const items: FlashBookShelfItem[] = []
  for (const [bookUri, progress] of Object.entries(index)) {
    const item = shelfItemFor(bookUri, progress)
    if (item) items.push(item)
  }
  /*
   * Newest first, and *deterministically* so.
   *
   * The tie-break is not decoration: two books saved in the same millisecond are a
   * real possibility (a device with a coarse clock, or two writes in one tick), and
   * an order that fell back to `Object.entries` would follow insertion order and
   * quietly reshuffle the shelf as the index was rebuilt. A shelf that reorders
   * itself under the learner's thumb is worse than one that is occasionally wrong.
   *
   * A date that will not parse sorts last rather than throwing: one corrupt entry
   * must not take the whole shelf down with it.
   */
  return items
    .sort((a, b) => {
      const byTime = lastReadMs(b.lastReadAt) - lastReadMs(a.lastReadAt)
      if (byTime !== 0) return byTime
      return a.bookUri < b.bookUri ? -1 : a.bookUri > b.bookUri ? 1 : 0
    })
    .slice(0, Math.max(0, limit))
}

/**
 * One book's place, for a surface about to offer *this book*.
 *
 * The shelf asks "which books is this learner in the middle of?" and this asks
 * "should this card offer to continue?". They are close relatives and deliberately
 * not the same question. A feed card that says "Continue" has to be right about the
 * one book under the learner's thumb, and the reader is the authority on that: it
 * offers a continue exactly when `resumeTarget` finds one, and refuses one for a
 * book opened and set down on its first page with nothing answered — there is
 * nothing to continue there, and two buttons that do the same thing teach a learner
 * to trust neither.
 *
 * So the rule is borrowed rather than restated: the same function the player's own
 * cover uses decides whether this book has a place to go back to. A shelf, which is
 * a list of places to *enter* rather than one promise about one book, is looser and
 * keeps listing a book that was only opened.
 */
export function flashBookPlace(
  index: FlashBookProgressIndex | null | undefined,
  bookUri: string | null | undefined,
): FlashBookShelfItem | null {
  if (!index || !bookUri) return null
  const progress = index[bookUri]
  if (!progress || !resumeTarget(progress)) return null
  return shelfItemFor(bookUri, progress)
}

/** One book's place, as a shelf item — or `null` when there is nothing to continue. */
function shelfItemFor(
  bookUri: string,
  progress: FlashBookReadProgress | null | undefined,
): FlashBookShelfItem | null {
  const open = progress?.open
  if (!open || typeof open !== 'object') return null
  const {page, pageCount, pagesRead, pct} = placeOf(open)
  return {
    bookUri,
    bookTitle:
      typeof progress.bookTitle === 'string' ? progress.bookTitle.trim() : '',
    page,
    pageCount,
    pagesRead,
    pct,
    lastReadAt:
      typeof progress.updatedAt === 'string'
        ? progress.updatedAt
        : (open.startedAt ?? ''),
  }
}

/**
 * Where a read is, in pages.
 *
 * `pageCount` is guarded against zero and against nonsense, because it is what the
 * fraction divides by: `pageIndex / 0` would be `NaN`, and a progress rail drawn
 * from `NaN` is a bar at no width that still claims to be a bar. A read with no
 * pages at all — which no book produces, and which a corrupt entry can — keeps its
 * page number and loses its fraction.
 */
function placeOf(open: FlashBookOpenRead): {
  page: number
  pageCount: number
  pagesRead: number
  pct: number
} {
  const rawCount = Number(open.pageCount)
  const pageCount =
    Number.isFinite(rawCount) && rawCount > 0 ? Math.trunc(rawCount) : 0
  const rawIndex = Number(open.pageIndex)
  const index =
    Number.isFinite(rawIndex) && rawIndex > 0 ? Math.trunc(rawIndex) : 0
  if (pageCount === 0) {
    return {page: index + 1, pageCount: 0, pagesRead: index, pct: 0}
  }
  const pagesRead = Math.min(index, pageCount)
  return {
    page: Math.min(pagesRead, pageCount - 1) + 1,
    pageCount,
    pagesRead,
    pct: pagesRead / pageCount,
  }
}

/** An instant, or 0 when it will not parse — so a corrupt date sorts to the end. */
function lastReadMs(value: string): number {
  const ms = Date.parse(value)
  return Number.isFinite(ms) ? ms : 0
}
